import { Injectable } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class SecureStorageService {
  private readonly STORAGE_PREFIX = 'secure_';
  private readonly ENCRYPTED_FIELDS = ['token', 'refreshToken'];
  private cryptoKey: Promise<CryptoKey>; // Store the imported key

  constructor() {
    this.cryptoKey = this.importKey(environment.storageKey);
  }

  // --- NEW ASYNC SETTER ---
  async setItem<T>(key: string, data: T, encryptSensitive: boolean = true): Promise<boolean> {
    try {
      if (!data) {
        this.removeItem(key); // This can remain synchronous
        return true;
      }

      const dataCopy = JSON.parse(JSON.stringify(data));

      // Use a new object for the final stored data
      const storageData: any = {
        data: dataCopy,
        timestamp: Date.now()
      };

      if (encryptSensitive && typeof dataCopy === 'object') {
        // This method will now be async
        await this.obfuscateSensitiveFields(dataCopy);
      }
      
      // We no longer need a manual integrity hash; AES-GCM does it.
      const storageKey = this.getStorageKey(key);
      const serialized = JSON.stringify(storageData);
      
      // We must encrypt the *entire* serialized object, not just parts of it.
      // This protects the timestamp and the structure from being tampered with.
      const encrypted = await this.encrypt(serialized);
      localStorage.setItem(storageKey, encrypted);
      return true;

    } catch (error) {
      console.error('Error storing data securely:', error);
      return false;
    }
  }

  // --- NEW ASYNC GETTER ---
  async getItem<T>(key: string, checkTokenExpiration: boolean = true): Promise<T | null> {
    try {
      const storageKey = this.getStorageKey(key);
      const stored = localStorage.getItem(storageKey);
      
      if (!stored) {
        return null;
      }

      // We decrypt the *entire* object
      let decrypted: string;
      try {
        decrypted = await this.decrypt(stored);
      } catch (e) {
        // If decryption fails, the data was tampered with or the key changed.
        console.error('Failed to decrypt data. It may be corrupt or tampered with.', e);
        this.removeItem(key);
        return null;
      }

      const storageData = JSON.parse(decrypted);

      // We no longer need validateIntegrity, decryption failure *is* the integrity check.

      const data = storageData.data;

      // We de-obfuscate *after* parsing
      if (typeof data === 'object' && data !== null) {
        await this.deobfuscateSensitiveFields(data);
      }

      // Your existing validation logic is still good
      if (key === 'currentUser' && data) {
        if (!this.validateUserData(data)) {
          this.removeItem(key);
          return null;
        }
      }
      if (checkTokenExpiration && key === 'currentUser' && data?.token) {
        if (this.isTokenExpired(data.token)) {
          this.removeItem(key);
          return null;
        }
      }

      return data as T;
    } catch (error) {
      // If parsing fails or anything else, remove the bad data
      this.removeItem(key);
      return null;
    }
  }

  removeItem(key: string): void {
    try {
      const storageKey = this.getStorageKey(key);
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error removing data securely:', error);
    }
  }

  isTokenExpired(token: string): boolean {
    try {
      if (!token) {
        return true;
      }

      const decoded = jwtDecode<any>(token);
      if (!decoded || !decoded.exp) {
        return true;
      }

      const expirationTime = decoded.exp * 1000; // Convert to milliseconds
      const currentTime = Date.now();
      
      // Add 5 minute buffer to account for clock skew
      const bufferTime = 5 * 60 * 1000;
      
      return currentTime >= (expirationTime - bufferTime);
    } catch (error) {
      console.error('Error checking token expiration:', error);
      return true;
    }
  }

  validateToken(token: string): boolean {
    return !this.isTokenExpired(token);
  }

  clear(): void {
    try {
      const keys = Object.keys(localStorage);
      keys.forEach(key => {
        if (key.startsWith(this.STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing secure storage:', error);
    }
  }

  // ######################################################
  // ##           REPLACEMENT CRYPTO FUNCTIONS           ##
  // ######################################################

  /**
   * Imports the environment key string into a usable CryptoKey object.
   * AES-GCM requires a key of exactly 16, 24, or 32 bytes.
   * We use AES-256, so we need exactly 32 bytes.
   */
  private async importKey(keyString: string): Promise<CryptoKey> {
    // Convert string to bytes
    const keyBytes = new TextEncoder().encode(keyString);
    
    // Ensure key is exactly 32 bytes for AES-256
    let finalKeyBytes: Uint8Array;
    if (keyBytes.length === 32) {
      // Create a new ArrayBuffer to avoid type issues
      const buffer = new ArrayBuffer(32);
      finalKeyBytes = new Uint8Array(buffer);
      finalKeyBytes.set(keyBytes);
    } else if (keyBytes.length < 32) {
      // Pad with zeros if too short
      finalKeyBytes = new Uint8Array(32);
      finalKeyBytes.set(keyBytes, 0);
    } else {
      // Truncate if too long
      const buffer = new ArrayBuffer(32);
      finalKeyBytes = new Uint8Array(buffer);
      finalKeyBytes.set(keyBytes.slice(0, 32));
    }
    
    return window.crypto.subtle.importKey(
      'raw',
      finalKeyBytes as BufferSource,
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Encrypts a plaintext string using AES-GCM.
   * The IV (Initialization Vector) is prepended to the ciphertext.
   */
  private async encrypt(plaintext: string): Promise<string> {
    const key = await this.cryptoKey;
    const iv = window.crypto.getRandomValues(new Uint8Array(12)); // 96-bit IV
    const encodedData = new TextEncoder().encode(plaintext);

    const encryptedBuffer = await window.crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encodedData
    );

    // We need to store the IV with the ciphertext to decrypt it.
    // We'll join them and base64-encode the result.
    const ivArray = Array.from(iv);
    const encryptedArray = Array.from(new Uint8Array(encryptedBuffer));
    
    // Store as: iv.ciphertext
    const combined = JSON.stringify({ iv: ivArray, data: encryptedArray });
    return btoa(combined); // Base64 encode to safely store in localStorage
  }

  /**
   * Decrypts a base64-encoded string (IV + ciphertext) using AES-GCM.
   */
  private async decrypt(base64Ciphertext: string): Promise<string> {
    const key = await this.cryptoKey;
    
    const combined = JSON.parse(atob(base64Ciphertext));
    const iv = new Uint8Array(combined.iv);
    const encryptedData = new Uint8Array(combined.data);

    const decryptedBuffer = await window.crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: iv },
      key,
      encryptedData
    );

    return new TextDecoder().decode(decryptedBuffer);
  }

  // ######################################################
  // ##           OBFUSCATION FUNCTIONS                  ##
  // ######################################################

  /**
   * Obfuscates sensitive fields in an object
   * Note: This is still used for additional obfuscation layer
   */
  private async obfuscateSensitiveFields(obj: any): Promise<void> {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    for (const field of this.ENCRYPTED_FIELDS) {
      if (obj[field] && typeof obj[field] === 'string') {
        // Encrypt the sensitive field value
        const encrypted = await this.encrypt(obj[field]);
        obj[field] = encrypted;
        obj[`${field}_obfuscated`] = true; // Flag to indicate obfuscation
      }
    }
  }

  /**
   * Deobfuscates sensitive fields in an object
   */
  private async deobfuscateSensitiveFields(obj: any): Promise<void> {
    if (!obj || typeof obj !== 'object') {
      return;
    }

    for (const field of this.ENCRYPTED_FIELDS) {
      if (obj[`${field}_obfuscated`] && obj[field]) {
        try {
          obj[field] = await this.decrypt(obj[field]);
          delete obj[`${field}_obfuscated`];
        } catch (error) {
          // If decryption fails, keep the encrypted value (shouldn't happen)
          console.error(`Failed to deobfuscate field ${field}:`, error);
        }
      }
    }
  }

  // ######################################################
  // ##           VALIDATION FUNCTIONS                   ##
  // ######################################################

  /**
   * Validates user data structure
   */
  private validateUserData(data: any): boolean {
    if (!data || typeof data !== 'object') {
      return false;
    }

    // Basic validation - check for required fields
    if (data.token && typeof data.token !== 'string') {
      return false;
    }

    if (data.refreshToken && typeof data.refreshToken !== 'string') {
      return false;
    }

    return true;
  }

  /**
   * Gets the storage key with prefix
   */
  private getStorageKey(key: string): string {
    return `${this.STORAGE_PREFIX}${key}`;
  }
}
